<script setup>
import { onMounted, ref } from "vue";
import {
  ArrowRight,
  Avatar,
  Connection,
  Document,
  Key,
  Lightning,
  Lock,
  RefreshLeft,
  User,
} from "@element-plus/icons-vue";
import Notification from "@/utils/plugins/NotificationService";
import { useRouter } from "vue-router";
import EmployeeService from "@/api/services/employee.service";
import UsersExtranetService from "@/api/services/usersExtranet";
import ApiApplicationService from "@/api/services/apiApplication.service";
import AuditLogService from "@/api/services/auditLog.service";
import { canAccessSecurityAudit, canAccessImpersonations } from "@/utils/securityAccess";
import { useAuthStore } from "@/app/stores/authStore";

const router = useRouter();
const authStore = useAuthStore();

const summaryCards = ref([
  { label: "Usuarios internos", total: 0, active: 0, secondaryLabel: "Inactivos", secondary: 0, icon: User, route: "security-internal-users" },
  { label: "Usuarios externos", total: 0, active: 0, secondaryLabel: "Inactivos", secondary: 0, icon: Connection, route: "security-external-users" },
  { label: "API keys", total: 0, active: 0, secondaryLabel: "Revocadas", secondary: 0, icon: Key, route: "security-api-applications" },
]);

const failedLogins = ref([]);
const permissionChanges = ref([]);
const impersonations = ref([]);
const unusedApiKeys = ref([]);

const canSeeAudit = canAccessSecurityAudit(authStore.getUserLogged?.roles, authStore.getUserLogged?.permissions);
const canSeeImpersonations = canAccessImpersonations(authStore.getUserLogged?.roles, authStore.getUserLogged?.permissions);

const quickActions = [
  { title: "Gestionar usuarios internos", description: "Crear, editar y desactivar usuarios", icon: User, route: "security-internal-users" },
  { title: "Gestionar usuarios externos", description: "Invitar y administrar accesos", icon: Connection, route: "security-external-users" },
  { title: "Perfiles de permisos", description: "Gestionar perfiles y asignaciones", icon: Lock, route: "security-permission-profiles" },
  { title: "Catálogo de permisos", description: "Explorar y gestionar permisos", icon: Document, route: "security-permission-catalog" },
  { title: "Aplicaciones API", description: "Crear y administrar aplicaciones", icon: Key, route: "security-api-applications" },
  { title: "Auditoría de seguridad", description: "Revisar eventos y actividad", icon: RefreshLeft, route: "security-audit" },
];

const showPendingMessage = (label) => {
  Notification.info(`${label} estará disponible próximamente.`, 3500);
};

const handleQuickAction = (action) => {
  if (action.route) {
    router.push({ name: action.route });
    return;
  }
  showPendingMessage(action.title);
};

const handleSummaryCard = (card) => {
  if (card.route) {
    router.push({ name: card.route });
  }
};

const actorLabel = (actorType, actorId) => {
  if (!actorType && !actorId) return "Sistema";
  const typeLabel = { user: "Empleado", extranet_user: "Usuario externo", api_application: "Aplicación externa" };
  return `${typeLabel[actorType] ?? actorType} #${actorId ?? "—"}`;
};

const relativeTime = (iso) => {
  if (!iso) return "Nunca";
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} día${days === 1 ? "" : "s"}`;
};

const loadInternalUsersSummary = () => {
  Promise.all([
    EmployeeService.getEmployees({ itemsPerPage: 1 }),
    EmployeeService.getEmployees({ itemsPerPage: 1, active: 1 }),
  ]).then(([totalRes, activeRes]) => {
    if (!totalRes || !activeRes) return;
    const total = totalRes.data["hydra:totalItems"] ?? 0;
    const active = activeRes.data["hydra:totalItems"] ?? 0;
    summaryCards.value[0].total = total;
    summaryCards.value[0].active = active;
    summaryCards.value[0].secondary = total - active;
  });
};

const loadExternalUsersSummary = () => {
  Promise.all([
    UsersExtranetService.getUsers({ itemsPerPage: 1 }),
    UsersExtranetService.getUsers({ itemsPerPage: 1, active: 1 }),
  ]).then(([totalRes, activeRes]) => {
    if (!totalRes || !activeRes) return;
    const total = totalRes.data["hydra:totalItems"] ?? 0;
    const active = activeRes.data["hydra:totalItems"] ?? 0;
    summaryCards.value[1].total = total;
    summaryCards.value[1].active = active;
    summaryCards.value[1].secondary = total - active;
  });
};

const loadApiKeysSummary = () => {
  ApiApplicationService.getApiApplications().then((res) => {
    if (!res) return;
    const applications = res.data ?? [];
    const revoked = applications.filter((app) => app.revokedAt).length;
    const active = applications.filter((app) => !app.revokedAt && app.currentlyValid).length;
    summaryCards.value[2].total = applications.length;
    summaryCards.value[2].active = active;
    summaryCards.value[2].secondary = revoked;

    unusedApiKeys.value = applications
      .filter((app) => !app.revokedAt)
      .sort((a, b) => new Date(a.lastUsedAt ?? 0) - new Date(b.lastUsedAt ?? 0))
      .slice(0, 5)
      .map((app) => ({ name: app.name, lastUse: relativeTime(app.lastUsedAt) }));
  });
};

const loadFailedLogins = () => {
  if (!canSeeAudit) return;
  AuditLogService.getAuditLogs({
    category: "auth", action: "login_failed", itemsPerPage: 5, "order[createdAt]": "desc",
  }).then((res) => {
    if (!res) return;
    failedLogins.value = (res.data["hydra:member"] ?? []).map((event) => ({
      id: event.id, ip: event.ip ?? "—", time: relativeTime(event.createdAt),
    }));
  });
};

const loadPermissionChanges = () => {
  if (!canSeeAudit) return;
  AuditLogService.getAuditLogs({
    category: "permission_change", itemsPerPage: 5, "order[createdAt]": "desc",
  }).then((res) => {
    if (!res) return;
    permissionChanges.value = (res.data["hydra:member"] ?? []).map((event) => ({
      id: event.id, actor: actorLabel(event.actorType, event.actorId), action: event.action, time: relativeTime(event.createdAt),
    }));
  });
};

const loadImpersonations = () => {
  if (!canSeeAudit) return;
  AuditLogService.getAuditLogs({
    category: "impersonation", action: "impersonation_started", itemsPerPage: 5, "order[createdAt]": "desc",
  }).then((res) => {
    if (!res) return;
    impersonations.value = (res.data["hydra:member"] ?? []).map((event) => ({
      id: event.id,
      source: actorLabel(event.actorType, event.actorId),
      target: actorLabel(event.actingAsType, event.actingAsId),
      time: relativeTime(event.createdAt),
    }));
  });
};

onMounted(() => {
  loadInternalUsersSummary();
  loadExternalUsersSummary();
  loadApiKeysSummary();
  loadFailedLogins();
  loadPermissionChanges();
  loadImpersonations();
});
</script>

<template>
  <section class="security-dashboard">
    <header class="security-dashboard__header">
      <div>
        <h1>Seguridad <span class="security-dashboard__section">· Resumen</span></h1>
        <p>Visión general del estado de seguridad de tu entorno.</p>
      </div>
    </header>

    <div class="row security-dashboard__summary">
      <div v-for="card in summaryCards" :key="card.label" class="col-12 col-md-4 security-dashboard__column">
        <article class="security-summary-card" :class="{ 'security-summary-card--interactive': card.route }"
          :tabindex="card.route ? 0 : undefined" :role="card.route ? 'button' : undefined"
          @click="handleSummaryCard(card)" @keydown.enter="handleSummaryCard(card)">
          <el-icon class="security-summary-card__icon">
            <component :is="card.icon" />
          </el-icon>
          <div class="security-summary-card__content">
            <p>{{ card.label }}</p>
            <strong>{{ card.total }}</strong>
            <span>Activos: <b>{{ card.active }}</b> <i aria-hidden="true">•</i> {{ card.secondaryLabel }}: {{
              card.secondary }}</span>
          </div>
          <el-icon class="security-summary-card__arrow">
            <ArrowRight />
          </el-icon>
        </article>
      </div>
    </div>

    <div class="row security-dashboard__activity">
      <div class="col-12 col-xl-4 security-dashboard__column">
        <article class="security-panel security-panel--list">
          <div class="security-panel__heading">
            <h2><el-icon>
                <Lock />
              </el-icon> Últimos logins fallidos</h2>
            <button v-if="canSeeAudit" type="button" class="security-panel__link"
              @click="router.push({ name: 'security-audit' })">Ver todo</button>
          </div>
          <ul v-if="canSeeAudit" class="security-event-list">
            <li v-for="login in failedLogins" :key="login.id">
              <span class="security-event-list__icon security-event-list__icon--danger">×</span>
              <span class="security-event-list__main">Intento fallido</span>
              <span class="security-event-list__ip">{{ login.ip }}</span>
              <small>{{ login.time }}</small>
            </li>
            <li v-if="!failedLogins.length" class="security-event-list__empty">Sin eventos recientes.</li>
          </ul>
          <p v-else class="security-event-list__empty">No tienes permiso para ver la auditoría.</p>
        </article>
      </div>

      <div class="col-12 col-xl-4 security-dashboard__column">
        <article class="security-panel security-panel--list">
          <div class="security-panel__heading">
            <h2><el-icon>
                <Lock />
              </el-icon> Cambios de permisos</h2>
            <button v-if="canSeeAudit" type="button" class="security-panel__link"
              @click="router.push({ name: 'security-audit' })">Ver todo</button>
          </div>
          <ul v-if="canSeeAudit" class="security-event-list security-event-list--details">
            <li v-for="change in permissionChanges" :key="change.id">
              <span class="security-event-list__icon security-event-list__icon--warning"><el-icon>
                  <User />
                </el-icon></span>
              <span class="security-event-list__main"><b>{{ change.actor }}</b><em>{{ change.action }}</em></span>
              <small>{{ change.time }}</small>
            </li>
            <li v-if="!permissionChanges.length" class="security-event-list__empty">Sin eventos recientes.</li>
          </ul>
          <p v-else class="security-event-list__empty">No tienes permiso para ver la auditoría.</p>
        </article>
      </div>

      <div class="col-12 col-xl-4 security-dashboard__column">
        <article class="security-panel security-panel--list">
          <div class="security-panel__heading">
            <h2><el-icon>
                <Avatar />
              </el-icon> Últimas impersonaciones</h2>
            <button v-if="canSeeImpersonations" type="button" class="security-panel__link"
              @click="router.push({ name: 'security-impersonations' })">Ver todo</button>
          </div>
          <ul v-if="canSeeAudit" class="security-event-list security-event-list--impersonations">
            <li v-for="impersonation in impersonations" :key="impersonation.id">
              <span class="security-event-list__icon"><el-icon>
                  <User />
                </el-icon></span>
              <span class="security-event-list__main">{{ impersonation.source }}</span>
              <el-icon class="security-event-list__direction">
                <ArrowRight />
              </el-icon>
              <span class="security-event-list__target">{{ impersonation.target }}</span>
              <small>{{ impersonation.time }}</small>
            </li>
            <li v-if="!impersonations.length" class="security-event-list__empty">Sin eventos recientes.</li>
          </ul>
          <p v-else class="security-event-list__empty">No tienes permiso para ver la auditoría.</p>
        </article>
      </div>
    </div>

    <div class="row security-dashboard__bottom">
      <div class="col-12 col-xl-6 security-dashboard__column">
        <article class="security-panel security-api-keys">
          <div class="security-panel__heading">
            <h2><el-icon>
                <Key />
              </el-icon> API keys con menor uso reciente</h2>
            <button type="button" class="security-panel__link"
              @click="router.push({ name: 'security-api-applications' })">Ver todas</button>
          </div>
          <div class="security-api-keys__table" role="table" aria-label="API keys con menor uso reciente">
            <div class="security-api-keys__row security-api-keys__row--head" role="row">
              <span>Nombre</span><span>Último uso</span>
            </div>
            <div v-for="apiKey in unusedApiKeys" :key="apiKey.name" class="security-api-keys__row" role="row">
              <span data-label="Nombre">{{ apiKey.name }}</span><span data-label="Último uso">{{ apiKey.lastUse }}</span>
            </div>
            <p v-if="!unusedApiKeys.length" class="security-event-list__empty">No hay aplicaciones activas.</p>
          </div>
        </article>
      </div>

      <div class="col-12 col-xl-6 security-dashboard__column">
        <article class="security-panel security-quick-actions">
          <div class="security-panel__heading">
            <h2><el-icon>
                <Lightning />
              </el-icon> Acciones rápidas</h2>
          </div>
          <div class="security-quick-actions__grid">
            <button v-for="action in quickActions" :key="action.title" type="button" class="security-quick-action"
              @click="handleQuickAction(action)">
              <el-icon class="security-quick-action__icon">
                <component :is="action.icon" />
              </el-icon>
              <span><b>{{ action.title }}</b><small>{{ action.description }}</small></span>
              <el-icon>
                <ArrowRight />
              </el-icon>
            </button>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.security-dashboard {
  color: #27305e;
}

.security-dashboard__header {
  margin-bottom: 18px;
}

.security-dashboard h1 {
  color: #474ce6;
  font-size: clamp(2.2rem, 4vw, 3.7rem);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 1;
  margin: 0 0 8px;
}

.security-dashboard__section {
  color: #474ce6;
  font-size: .52em;
  letter-spacing: -0.035em;
  white-space: nowrap;
}

.security-dashboard__header>div>p:last-child {
  color: #7c82a4;
  margin: 0;
}

.security-dashboard__column {
  margin-bottom: 20px;
}

.security-summary-card,
.security-panel {
  background: #fff;
  border: 1px solid #e6e7f3;
  border-radius: 9px;
  box-shadow: 0 5px 16px rgba(42, 47, 106, .09);
}

.security-summary-card {
  align-items: center;
  display: flex;
  min-height: 112px;
  padding: 16px;
}

.security-summary-card--interactive {
  cursor: pointer;
  transition: box-shadow .2s ease, transform .2s ease;
}

.security-summary-card--interactive:hover,
.security-summary-card--interactive:focus-visible {
  box-shadow: 0 8px 20px rgba(71, 76, 230, .18);
  outline: 0;
  transform: translateY(-2px);
}

.security-summary-card__icon,
.security-quick-action__icon {
  align-items: center;
  background: #eff0ff;
  border-radius: 16px;
  color: #474ce6;
  display: inline-flex;
  flex: 0 0 74px;
  font-size: 34px;
  height: 74px;
  justify-content: center;
}

.security-summary-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 16px;
  min-width: 0;
}

.security-summary-card__content p {
  color: #474ce6;
  font-weight: 700;
  margin: 0;
}

.security-summary-card__content strong {
  color: #474ce6;
  font-size: 2rem;
  line-height: 1.15;
}

.security-summary-card__content span {
  color: #747b9c;
  font-size: .85rem;
  white-space: nowrap;
}

.security-summary-card__content b {
  color: #31975b;
}

.security-summary-card__content i {
  color: #727897;
  font-style: normal;
  margin: 0 7px;
}

.security-summary-card__arrow {
  color: #c2c7d9;
  font-size: 23px;
}

.security-panel {
  height: 100%;
  padding: 17px 18px;
}

.security-panel--list {
  display: flex;
  flex-direction: column;
  min-height: 286px;
}

.security-panel__heading {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 13px;
}

.security-panel__heading h2 {
  align-items: center;
  color: #474ce6;
  display: flex;
  font-size: 1rem;
  font-weight: 800;
  gap: 10px;
  margin: 0;
}

.security-panel__heading h2 .el-icon {
  font-size: 22px;
}

.security-panel__link {
  background: transparent;
  border: 0;
  color: #474ce6;
  cursor: pointer;
  font-size: .77rem;
  font-weight: 700;
  padding: 3px;
}

.security-panel__link:hover,
.security-panel__link:focus-visible {
  color: #f9156f;
  text-decoration: underline;
}

.security-event-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.security-event-list__empty {
  color: #8e94ad;
  font-size: .78rem;
  margin: 8px 0;
}

.security-event-list li {
  align-items: center;
  display: grid;
  font-size: .75rem;
  gap: 8px;
  grid-template-columns: 19px minmax(0, 1fr) auto auto;
  min-height: 34px;
}

.security-event-list__icon {
  align-items: center;
  background: #eff0ff;
  border-radius: 50%;
  color: #474ce6;
  display: inline-flex;
  font-size: 12px;
  height: 19px;
  justify-content: center;
  width: 19px;
}

.security-event-list__icon--danger {
  background: #ffeaf1;
  color: #f9156f;
  font-weight: 800;
}

.security-event-list__icon--warning {
  background: #fff7d9;
  color: #e6ae00;
}

.security-event-list__main {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-event-list__ip {
  color: #596384;
}

.security-event-list small {
  color: #8e94ad;
  font-size: .65rem;
  text-align: right;
  white-space: nowrap;
}

.security-event-list--details li {
  grid-template-columns: 21px minmax(0, 1fr) auto;
  min-height: 40px;
}

.security-event-list--details em {
  color: #8087a2;
  display: block;
  font-size: .7rem;
  font-style: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-event-list--details b {
  font-weight: 700;
}

.security-event-list--impersonations li {
  grid-template-columns: 19px minmax(0, 1fr) 14px minmax(0, 1fr) auto;
}

.security-event-list__direction {
  color: #7a82a2;
  font-size: 13px;
}

.security-event-list__target {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-panel__outline-button {
  align-self: center;
  background: #fff;
  border: 1px solid #f9156f;
  border-radius: 18px;
  color: #f9156f;
  cursor: pointer;
  font-size: .76rem;
  font-weight: 700;
  margin-top: auto;
  padding: 6px 21px;
  text-transform: none;
}

.security-panel__outline-button:hover,
.security-panel__outline-button:focus-visible {
  background: #f9156f;
  color: #fff;
}

.security-api-keys__table {
  font-size: .77rem;
}

.security-api-keys__row {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 1.7fr 1fr;
  min-height: 37px;
}

.security-api-keys__row--head {
  color: #474ce6;
  font-size: .68rem;
  font-weight: 800;
}

.security-api-keys__row:not(.security-api-keys__row--head) {
  border-top: 1px solid #f0f1f7;
}

.security-quick-actions__grid {
  display: grid;
  gap: 9px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.security-quick-action {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7f0;
  border-radius: 6px;
  color: #a2a8bd;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: 43px minmax(0, 1fr) 16px;
  min-height: 64px;
  padding: 8px 10px;
  text-align: left;
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}

.security-quick-action:hover,
.security-quick-action:focus-visible {
  border-color: #474ce6;
  box-shadow: 0 4px 12px rgba(71, 76, 230, .14);
  outline: 0;
  transform: translateY(-1px);
}

.security-quick-action__icon {
  border-radius: 10px;
  flex-basis: 43px;
  font-size: 22px;
  height: 43px;
}

.security-quick-action span {
  min-width: 0;
}

.security-quick-action b,
.security-quick-action small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-quick-action b {
  color: #474ce6;
  font-size: .77rem;
}

.security-quick-action small {
  color: #9ba1b5;
  font-size: .68rem;
  margin-top: 3px;
}

@media (max-width: 1199px) {
  .security-panel--list {
    min-height: 260px;
  }
}

@media (max-width: 767px) {
  .security-dashboard__column {
    margin-bottom: 14px;
  }

  .security-summary-card {
    min-height: 96px;
  }

  .security-summary-card__icon {
    flex-basis: 61px;
    font-size: 29px;
    height: 61px;
  }

  .security-summary-card__content {
    margin-left: 12px;
  }

  .security-panel {
    padding: 15px 13px;
  }

  .security-panel--list {
    min-height: auto;
  }

  .security-event-list li {
    grid-template-columns: 19px minmax(0, 1fr) auto;
    padding: 4px 0;
  }

  .security-event-list__ip {
    grid-column: 2;
  }

  .security-event-list li small {
    grid-column: 3;
    grid-row: 1 / span 2;
  }

  .security-event-list--details li {
    grid-template-columns: 21px minmax(0, 1fr) auto;
  }

  .security-event-list--impersonations li {
    grid-template-columns: 19px minmax(0, 1fr) 14px;
  }

  .security-event-list--impersonations .security-event-list__target {
    grid-column: 2 / span 2;
  }

  .security-event-list--impersonations li small {
    grid-column: 3;
    grid-row: 1;
  }

  .security-api-keys__row {
    gap: 4px 10px;
    padding: 8px 0;
  }

  .security-api-keys__row--head {
    display: none;
  }

  .security-api-keys__row span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .security-api-keys__row span::before {
    color: #7c83a1;
    content: attr(data-label);
    font-size: .62rem;
    font-weight: 800;
  }

  .security-quick-actions__grid {
    grid-template-columns: 1fr;
  }

  .security-quick-action {
    min-height: 60px;
  }
}
</style>
